import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { Loading, Flex, Tooltip } from 'components/patterns'
import NoModulesIllustration from 'img/empty-modules.svg'
import XmcTooltipIcon from 'img/icon-xmc-tooltip.svg'
import strings from 'utils/strings'
import { getUserSetting } from 'selectors/user'
import USER_SETTINGS from 'constants/userSettings'
import { useSelector } from 'hooks/redux'
import { uniqBy } from 'ramda'
import FeaturesTable from './FeaturesTable'
import {
  NoModulesSection,
  NoModulesText,
  StyledTitle,
  SectionSubTitle,
} from './ChecPlusVehicleSummary.style'
import ChecPlusVehicleSummaryModulesContanier from './ChecPlusVehicleSummaryPane/ChecPlusVehicleSummaryModulesContanier'
import ChecPlusVehicleSummaryExpandedModulesContanier from './ChecPlusVehicleSummaryPane/ChecPlusVehicleSummaryExpandedModulesContainer'
import DynamicFeatureDetailsView from './DynamicFeatureDetailsView'

const NoModules = (props) => (
  <NoModulesSection
    flexDirection="column"
    gap="16px"
    justifyContent="center"
    alignItems="center"
    margin="16px 0 0"
  >
    <img alt="no modules" src={NoModulesIllustration} />
    <Flex justifyContent="center" alignItems="flex-start" gap="6px">
      <NoModulesText>{strings.no_modules_text(props?.isQPM)}</NoModulesText>
      {props?.isQPM && props?.showXmcTooltip && (
        <Tooltip
          placement="top"
          title={strings.xmc_available_tooltip}
          overlayClassName="xmc-tooltip"
          trigger={['hover', 'focus']}
          childWrapStyleProps={{ display: 'inline-flex', alignItems: 'center' }}
        >
          <img
            alt="XMC availability info"
            src={XmcTooltipIcon}
            tabIndex={0}
            aria-label={strings.xmc_available_tooltip}
            style={{ width: 14, height: 14, cursor: 'pointer' }}
          />
        </Tooltip>
      )}
    </Flex>
  </NoModulesSection>
)

NoModules.propTypes = {
  isQPM: PropTypes.bool,
  showXmcTooltip: PropTypes.bool,
}

const ChecPlusFeaturesAndModulesSection = ({
  sectionTitle,
  sectionSubTitle,
  showCards,
  vehicleECUs,
  features,
  icon,
  handleOnModuleSelect,
  selectedModuleName,
  vehicle,
  tableProps,
  isQPM,
  headerAction,
  showXmcTooltip,
  vehicleSubType,
  vehicle35mCode,
  buildDate,
}) => {
  const [selectedDynamicFeature, setSelectedDynamicFeature] = useState(null)
  const isModuleSummaryViewExpandedSetting = useSelector(
    getUserSetting,
    USER_SETTINGS.ENABLE_CHEC_PLUS_MODULE_SUMMARY_EXPANDED_VIEW
  )
  const isModuleSummaryViewExpanded =
    typeof isModuleSummaryViewExpandedSetting === 'boolean'
      ? isModuleSummaryViewExpandedSetting
      : true

  const VehicleSummaryComponent = isModuleSummaryViewExpanded
    ? ChecPlusVehicleSummaryModulesContanier
    : ChecPlusVehicleSummaryExpandedModulesContanier

  let shouldShowNoModulesView = !vehicleECUs?.length && showCards

  let shouldShowFeaturesWhenEmpty = !shouldShowNoModulesView && showCards

  const uniqFeatures = features
    ? uniqBy((feature) => feature.id, features)
    : null

  if (!showCards) {
    shouldShowNoModulesView = !vehicleECUs?.length && !uniqFeatures?.length
    shouldShowFeaturesWhenEmpty = !shouldShowNoModulesView
  }

  return (
    <>
      <Flex alignItems="flex-end" gap="16px" margin="25px 0 0">
        <Flex flexDirection="column" gap="8px" flex="1">
          <Flex gap="12px">
            <img alt="module icon" src={icon} />
            <StyledTitle type="secondary" level={4}>
              {sectionTitle}
            </StyledTitle>
          </Flex>
          <SectionSubTitle>{sectionSubTitle}</SectionSubTitle>
        </Flex>
        {headerAction}
      </Flex>

      {shouldShowNoModulesView ? (
        <NoModules isQPM={isQPM} showXmcTooltip={showXmcTooltip} />
      ) : (
        showCards && (
          <VehicleSummaryComponent
            vehicle={vehicle}
            vehicleModules={vehicleECUs}
            setSelected={handleOnModuleSelect}
            selected={selectedModuleName || ''}
          />
        )
      )}

      {!uniqFeatures ? <Loading contained size="sm" /> : null}

      {uniqFeatures && shouldShowFeaturesWhenEmpty ? (
        <>
          <FeaturesTable
            features={uniqFeatures}
            {...tableProps}
            vehicleECUs={vehicleECUs}
            vehicleSubType={vehicleSubType}
            vehicle35mCode={vehicle35mCode}
            buildDate={buildDate}
            onDynamicFeatureClick={setSelectedDynamicFeature}
          />
          {selectedDynamicFeature && (
            <DynamicFeatureDetailsView
              featureDetails={selectedDynamicFeature}
              onClose={() => setSelectedDynamicFeature(null)}
            />
          )}
        </>
      ) : null}
    </>
  )
}

ChecPlusFeaturesAndModulesSection.propTypes = {
  sectionTitle: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  sectionSubTitle: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  showCards: PropTypes.bool,
  vehicleECUs: PropTypes.array,
  features: PropTypes.array,
  icon: PropTypes.string,
  handleOnModuleSelect: PropTypes.func,
  selectedModuleName: PropTypes.string,
  vehicle: PropTypes.object,
  tableProps: PropTypes.object,
  isQPM: PropTypes.bool,
  headerAction: PropTypes.node,
  showXmcTooltip: PropTypes.bool,
  vehicleSubType: PropTypes.string,
  vehicle35mCode: PropTypes.string,
  buildDate: PropTypes.string,
}

export default ChecPlusFeaturesAndModulesSection
